import { 
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  getDocs,
  getDoc,
  getCountFromServer,
  updateDoc,
  doc,
  documentId,
  Timestamp,
  QueryConstraint,
  DocumentSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { Customer, CustomerFilters, CustomerStatsLite, Address } from '../models/Customer';

type ListCustomersResult = {
  customers: Customer[];
  nextCursor?: DocumentSnapshot;
};

type CountCustomersResult = {
  count: number;
};

export type CustomerScopeAggregate = {
  scopeType: 'store' | 'group' | 'global';
  scopeId: string;
};

class CustomerService {
  private readonly collectionName = 'users';
  private readonly scopeStatsCollectionName = 'customer_scope_stats';
  private readonly customerStatsCache = new Map<string, CustomerStatsLite>();
  private readonly customerStatsPromiseCache = new Map<
    string,
    Promise<CustomerStatsLite>
  >();

  private isEmailLike(term?: string): boolean {
    if (!term) return false;
    return term.includes('@');
  }

  private async isPhoneBanned(phoneE164?: string): Promise<boolean> {
    try {
      if (!phoneE164 || typeof phoneE164 !== 'string' || phoneE164.trim().length === 0) return false;
      const snap = await getDoc(doc(db, 'bannedPhones', phoneE164.trim()));
      return snap.exists();
    } catch {
      return false;
    }
  }

  private getStatsCacheKey(
    userId: string,
    dateRange?: { start: Date; end: Date }
  ): string {
    if (!dateRange?.start || !dateRange?.end) {
      return `${userId}::all`;
    }

    return `${userId}::${dateRange.start.getTime()}-${dateRange.end.getTime()}`;
  }

  private buildStatsFromOrders(orders: Array<{ totalAmount?: number; createdAt?: unknown }>): CustomerStatsLite {
    const totalOrders = orders.length;
    let totalSpent = 0;
    let lastOrderDate: Date | undefined;

    orders.forEach((order) => {
      totalSpent += order.totalAmount || 0;
      const createdAt = order.createdAt instanceof Timestamp
        ? order.createdAt.toDate()
        : (order.createdAt as { toDate?: () => Date } | undefined)?.toDate?.();

      if (createdAt && (!lastOrderDate || createdAt > lastOrderDate)) {
        lastOrderDate = createdAt;
      }
    });

    const daysSinceLast = lastOrderDate
      ? (Date.now() - lastOrderDate.getTime()) / (1000 * 60 * 60 * 24)
      : Number.POSITIVE_INFINITY;

    return {
      totalOrders,
      totalSpent,
      lastOrderDate,
      isActive: daysSinceLast <= 30,
    };
  }

  private async computeStatsForUsers(
    userIds: string[],
    dateRange?: { start: Date; end: Date }
  ): Promise<Map<string, CustomerStatsLite>> {
    const uniqueIds = Array.from(
      new Set(userIds.filter((userId) => userId && userId.trim().length > 0))
    );
    const statsByUser = new Map<string, CustomerStatsLite>();

    if (uniqueIds.length === 0) {
      return statsByUser;
    }

    const uncachedIds: string[] = [];
    uniqueIds.forEach((userId) => {
      const cacheKey = this.getStatsCacheKey(userId, dateRange);
      const cached = this.customerStatsCache.get(cacheKey);
      if (cached) {
        statsByUser.set(userId, cached);
      } else {
        uncachedIds.push(userId);
      }
    });

    if (uncachedIds.length === 0) {
      return statsByUser;
    }

    try {
      const chunkSize = 30;
      for (let index = 0; index < uncachedIds.length; index += chunkSize) {
        const chunk = uncachedIds.slice(index, index + chunkSize);
        const constraints: QueryConstraint[] = [where('userId', 'in', chunk)];

        if (dateRange?.start && dateRange?.end) {
          constraints.push(
            where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
            where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
          );
        }

        const q = query(collection(db, 'orders'), ...constraints);
        const snap = await getDocs(q);
        const ordersByUser = new Map<string, Array<{ totalAmount?: number; createdAt?: unknown }>>();

        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const userId = String(data.userId || '');
          if (!userId) return;
          const list = ordersByUser.get(userId) || [];
          list.push({
            totalAmount: data.totalAmount,
            createdAt: data.createdAt,
          });
          ordersByUser.set(userId, list);
        });

        chunk.forEach((userId) => {
          const userOrders = ordersByUser.get(userId) || [];
          const stats = this.buildStatsFromOrders(userOrders);
          const cacheKey = this.getStatsCacheKey(userId, dateRange);
          this.customerStatsCache.set(cacheKey, stats);
          statsByUser.set(userId, stats);
        });
      }

      return statsByUser;
    } catch (error) {
      const fallbackEntries = await Promise.all(
        uncachedIds.map(async (userId) => [
          userId,
          await this.computeStatsForUser(userId, dateRange),
        ] as const)
      );

      fallbackEntries.forEach(([userId, stats]) => {
        statsByUser.set(userId, stats);
      });

      return statsByUser;
    }
  }

  private async attachStatsToCustomers(
    baseCustomers: Customer[],
    dateRange?: { start: Date; end: Date }
  ): Promise<Customer[]> {
    const statsByUser = await this.computeStatsForUsers(
      baseCustomers.map((customer) => customer.id),
      dateRange
    );

    return baseCustomers.map((customer) => ({
      ...customer,
      stats: statsByUser.get(customer.id) || {
        totalOrders: 0,
        totalSpent: 0,
        lastOrderDate: undefined,
        isActive: false,
      },
    }));
  }

  // Helpers to detect/normalize phone search
  private isPhoneLike(term?: string): boolean {
    if (!term) return false;
    const digits = term.replace(/[^\d]/g, '');
    // consider phone-like if at least 3 digits and only digits/+ provided
    return /^[+\d][\d+\s]*$/.test(term) && digits.length >= 3;
  }

  private toE164Prefix(term: string): { prefix: string; isExact: boolean } | null {
    const raw = term.trim();
    const digits = raw.replace(/[^\d]/g, '');
    // Full exact +212[6|7]XXXXXXXX (13 chars incl '+')
    const exactE164 = raw.startsWith('+212') ? raw.replace(/\s+/g, '') : (raw.startsWith('212') ? `+${raw}` : '');
    if (/^\+212[67]\d{8}$/.test(exactE164)) {
      return { prefix: exactE164, isExact: true };
    }
    // Build +212 prefix when user types local 06/07... or 6/7...
    if (raw.startsWith('+212')) {
      const rest = raw.substring(4).replace(/\s+/g, '');
      return { prefix: `+212${rest}`, isExact: false };
    }
    if (raw.startsWith('212')) {
      const rest = raw.substring(3);
      return { prefix: `+212${rest}`, isExact: false };
    }
    // starts with 0 -> drop 0 and prefix +212
    if (raw.startsWith('0')) {
      const rest = raw.substring(1);
      return { prefix: `+212${rest}`, isExact: false };
    }
    // digits only starting with 6 or 7
    if (/^[67]\d*/.test(digits)) {
      return { prefix: `+212${digits}`, isExact: false };
    }
    // fallback: if user typed any digits, still try as +212 prefix
    if (digits.length >= 3) {
      return { prefix: `+212${digits}`, isExact: false };
    }
    return null;
  }

  // Convert Firestore doc to Customer
  private convertFirestoreToCustomer(docSnap: any): Customer {
    const data = docSnap.data() || {};
    const rawAddresses = Array.isArray(data.addresses) ? data.addresses : [];
    const addresses: Address[] | undefined = rawAddresses.length > 0
      ? rawAddresses.map((a: any) => ({
          id: String(a?.id ?? ''),
          city: String(a?.city ?? ''),
          region: a?.region ? String(a.region) : undefined,
          postalCode: a?.postalCode ? String(a.postalCode) : undefined,
          street: String(a?.street ?? ''),
          details: a?.details ? String(a.details) : undefined,
          latitude: typeof a?.latitude === 'number' ? a.latitude : Number(a?.latitude ?? 0),
          longitude: typeof a?.longitude === 'number' ? a.longitude : Number(a?.longitude ?? 0),
          isDefault: Boolean(a?.isDefault === true),
        }))
      : undefined;

    return {
      id: docSnap.id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone || '',
      email: data.email || '',
      isBanned: data.isBanned === true,
      addresses,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt instanceof Date ? data.createdAt : undefined),
    };
  }

  private convertScopeStatToCustomer(docSnap: any): Customer {
    const data = docSnap.data() || {};
    const userCreatedAt =
      data.userCreatedAt?.toDate ? data.userCreatedAt.toDate() :
      (data.userCreatedAt instanceof Date ? data.userCreatedAt : undefined);
    const lastOrderDate =
      data.lastOrderAt?.toDate ? data.lastOrderAt.toDate() :
      (data.lastOrderAt instanceof Date ? data.lastOrderAt : undefined);

    return {
      id: String(data.userId || docSnap.id),
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone || '',
      email: data.email || '',
      isBanned: data.isBanned === true,
      createdAt: userCreatedAt,
      stats: {
        totalOrders: typeof data.totalOrders === 'number' ? data.totalOrders : 0,
        totalSpent: typeof data.totalSpent === 'number' ? data.totalSpent : 0,
        lastOrderDate,
        isActive: data.isActive === true,
        storeIds: Array.isArray(data.storeIds)
          ? data.storeIds.filter((value: unknown): value is string => typeof value === 'string')
          : undefined,
        mostOrderedStore:
          typeof data.mostOrderedStore === 'string' && data.mostOrderedStore.trim()
            ? data.mostOrderedStore.trim()
            : undefined,
      },
    };
  }

  private applyScopedCustomerLocalFilters(
    customers: Customer[],
    filters?: CustomerFilters
  ): Customer[] {
    let filtered = [...customers];
    const term = filters?.searchTerm?.trim().toLowerCase();

    if (term) {
      filtered = filtered.filter((customer) => {
        const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim().toLowerCase();
        const phone = (customer.phone || '').toLowerCase();
        const email = (customer.email || '').toLowerCase();
        return fullName.includes(term) || phone.includes(term) || email.includes(term);
      });
    }

    if (filters?.hasEmail !== undefined) {
      filtered = filtered.filter((customer) =>
        filters.hasEmail ?
          !!(customer.email && customer.email.trim()) :
          !(customer.email && customer.email.trim())
      );
    }

    if (filters?.hasPhone !== undefined) {
      filtered = filtered.filter((customer) =>
        filters.hasPhone ?
          !!(customer.phone && customer.phone.trim()) :
          !(customer.phone && customer.phone.trim())
      );
    }

    if (filters?.isActive !== undefined) {
      filtered = filtered.filter(
        (customer) => (customer.stats?.isActive ?? false) === filters.isActive
      );
    }

    if (filters?.minOrders !== undefined) {
      const minOrders = filters.minOrders;
      filtered = filtered.filter(
        (customer) => (customer.stats?.totalOrders ?? 0) >= minOrders
      );
    }

    if (filters?.maxOrders !== undefined) {
      const maxOrders = filters.maxOrders;
      filtered = filtered.filter(
        (customer) => (customer.stats?.totalOrders ?? 0) <= maxOrders
      );
    }

    return filtered;
  }

  private async fetchAllScopedCustomers(
    scope: CustomerScopeAggregate,
    filters?: CustomerFilters
  ): Promise<Customer[]> {
    const baseConstraints: QueryConstraint[] = [
      where('scopeType', '==', scope.scopeType),
      where('scopeId', '==', scope.scopeId),
    ];

    if (filters?.bannedOnly) {
      baseConstraints.push(where('isBanned', '==', true));
    }

    let orderedConstraints: QueryConstraint[];
    if (filters?.dateRange?.start && filters?.dateRange?.end) {
      orderedConstraints = [
        ...baseConstraints,
        where('userCreatedAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
        where('userCreatedAt', '<=', Timestamp.fromDate(filters.dateRange.end)),
        orderBy('userCreatedAt', 'desc'),
      ];
    } else {
      orderedConstraints = [...baseConstraints, orderBy(documentId())];
    }

    const allDocs: Customer[] = [];
    let cursor: DocumentSnapshot | undefined;

    for (;;) {
      const constraints = [...orderedConstraints, limit(500)] as QueryConstraint[];
      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      const snap = await getDocs(
        query(collection(db, this.scopeStatsCollectionName), ...constraints)
      );

      if (snap.empty) {
        break;
      }

      allDocs.push(...snap.docs.map((docSnap) => this.convertScopeStatToCustomer(docSnap)));
      cursor = snap.docs[snap.docs.length - 1];

      if (snap.size < 500) {
        break;
      }
    }

    return allDocs;
  }

  async listScopedCustomers(
    scope: CustomerScopeAggregate,
    filters?: CustomerFilters,
    pageSize: number = 30,
    cursor?: DocumentSnapshot
  ): Promise<ListCustomersResult> {
    try {
      const baseConstraints: QueryConstraint[] = [
        where('scopeType', '==', scope.scopeType),
        where('scopeId', '==', scope.scopeId),
      ];

      if (filters?.bannedOnly) {
        baseConstraints.push(where('isBanned', '==', true));
      }

      const trimmedSearch = filters?.searchTerm?.trim() || '';
      const emailLike = this.isEmailLike(trimmedSearch);
      const phoneLike = this.isPhoneLike(trimmedSearch);

      if (phoneLike) {
        const normalized = this.toE164Prefix(trimmedSearch);
        if (normalized) {
          if (normalized.isExact) {
            const exactQuery = query(
              collection(db, this.scopeStatsCollectionName),
              ...baseConstraints,
              where('phone', '==', normalized.prefix),
              limit(pageSize)
            );
            const exactSnap = await getDocs(exactQuery);
            return {
              customers: exactSnap.docs.map((docSnap) => this.convertScopeStatToCustomer(docSnap)),
              nextCursor: undefined,
            };
          }

          const constraints = [
            ...baseConstraints,
            orderBy('phone'),
            startAt(normalized.prefix),
            endAt(`${normalized.prefix}\uf8ff`),
            limit(pageSize),
          ] as QueryConstraint[];

          if (cursor) {
            constraints.push(startAfter(cursor));
          }

          const snap = await getDocs(
            query(collection(db, this.scopeStatsCollectionName), ...constraints)
          );
          return {
            customers: snap.docs.map((docSnap) => this.convertScopeStatToCustomer(docSnap)),
            nextCursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : undefined,
          };
        }
      }

      if (emailLike) {
        const constraints = [
          ...baseConstraints,
          orderBy('email'),
          startAt(trimmedSearch),
          endAt(`${trimmedSearch}\uf8ff`),
          limit(pageSize),
        ] as QueryConstraint[];

        if (cursor) {
          constraints.push(startAfter(cursor));
        }

        const snap = await getDocs(
          query(collection(db, this.scopeStatsCollectionName), ...constraints)
        );
        return {
          customers: snap.docs.map((docSnap) => this.convertScopeStatToCustomer(docSnap)),
          nextCursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : undefined,
        };
      }

      if (trimmedSearch) {
        const constraints = [
          ...baseConstraints,
          orderBy('firstName'),
          startAt(trimmedSearch),
          endAt(`${trimmedSearch}\uf8ff`),
          limit(pageSize),
        ] as QueryConstraint[];

        if (cursor) {
          constraints.push(startAfter(cursor));
        }

        const snap = await getDocs(
          query(collection(db, this.scopeStatsCollectionName), ...constraints)
        );
        return {
          customers: snap.docs.map((docSnap) => this.convertScopeStatToCustomer(docSnap)),
          nextCursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : undefined,
        };
      }

      if (filters?.dateRange?.start && filters?.dateRange?.end) {
        baseConstraints.push(
          where('userCreatedAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('userCreatedAt', '<=', Timestamp.fromDate(filters.dateRange.end)),
          orderBy('userCreatedAt', 'desc')
        );
      }

      const constraints = [
        ...baseConstraints,
        orderBy('userCreatedAt', 'desc'),
        limit(pageSize),
      ] as QueryConstraint[];

      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      const snap = await getDocs(
        query(collection(db, this.scopeStatsCollectionName), ...constraints)
      );

      let customers = snap.docs.map((docSnap) => this.convertScopeStatToCustomer(docSnap));
      customers = this.applyScopedCustomerLocalFilters(customers, {
        hasEmail: filters?.hasEmail,
        hasPhone: filters?.hasPhone,
        isActive: filters?.isActive,
        minOrders: filters?.minOrders,
        maxOrders: filters?.maxOrders,
      });

      return {
        customers,
        nextCursor: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : undefined,
      };
    } catch (error) {
      console.error('Erreur listScopedCustomers:', error);
      throw new Error('Impossible de récupérer les clients indexés du scope');
    }
  }

  async countScopedCustomers(
    scope: CustomerScopeAggregate,
    filters?: CustomerFilters
  ): Promise<CountCustomersResult> {
    try {
      const needsExactLocalCount =
        !!filters?.searchTerm?.trim() ||
        filters?.hasEmail !== undefined ||
        filters?.hasPhone !== undefined ||
        filters?.isActive !== undefined ||
        filters?.minOrders !== undefined ||
        filters?.maxOrders !== undefined;

      if (needsExactLocalCount) {
        const customers = await this.fetchAllScopedCustomers(scope, filters);
        const filtered = this.applyScopedCustomerLocalFilters(customers, filters);
        return { count: filtered.length };
      }

      const baseConstraints: QueryConstraint[] = [
        where('scopeType', '==', scope.scopeType),
        where('scopeId', '==', scope.scopeId),
      ];

      if (filters?.bannedOnly) {
        baseConstraints.push(where('isBanned', '==', true));
      }
      if (filters?.dateRange?.start && filters?.dateRange?.end) {
        baseConstraints.push(
          where('userCreatedAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('userCreatedAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      const agg = await getCountFromServer(
        query(collection(db, this.scopeStatsCollectionName), ...baseConstraints)
      );
      return { count: Number(agg.data().count || 0) };
    } catch (error) {
      console.error('Erreur countScopedCustomers:', error);
      return { count: 0 };
    }
  }

  async rebuildScopedCustomersIndex(scope: CustomerScopeAggregate): Promise<{
    success: boolean;
    processedUsers: number;
    writtenScopes: number;
    deletedScopes: number;
  }> {
    const callable = httpsCallable(functions, 'rebuildCustomerScopeStats');
    const result = await callable(scope);
    const data = result.data as {
      success?: boolean;
      processedUsers?: number;
      writtenScopes?: number;
      deletedScopes?: number;
    };

    return {
      success: data.success === true,
      processedUsers: Number(data.processedUsers || 0),
      writtenScopes: Number(data.writtenScopes || 0),
      deletedScopes: Number(data.deletedScopes || 0),
    };
  }

  // Compute lightweight stats for one user by scanning orders
  private async computeStatsForUser(userId: string, dateRange?: { start: Date; end: Date }): Promise<CustomerStatsLite> {
    const cacheKey = this.getStatsCacheKey(userId, dateRange);
    const cachedStats = this.customerStatsCache.get(cacheKey);
    if (cachedStats) {
      return cachedStats;
    }

    const pendingStats = this.customerStatsPromiseCache.get(cacheKey);
    if (pendingStats) {
      return pendingStats;
    }

    const computePromise = (async (): Promise<CustomerStatsLite> => {
    try {
      const constraints: QueryConstraint[] = [where('userId', '==', userId)];

      if (dateRange?.start && dateRange?.end) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
        );
      }

      // Order by createdAt to get last order quickly (optional)
      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(collection(db, 'orders'), ...constraints);
      const snap = await getDocs(q);

      const totalOrders = snap.size;
      let totalSpent = 0;
      let lastOrderDate: Date | undefined;

      snap.docs.forEach((d, idx) => {
        const od = d.data();
        totalSpent += od.totalAmount || 0;
        if (idx === 0) {
          lastOrderDate = od.createdAt?.toDate ? od.createdAt.toDate() : undefined;
        }
      });

      const daysSinceLast = lastOrderDate ? ((Date.now() - lastOrderDate.getTime()) / (1000 * 60 * 60 * 24)) : Number.POSITIVE_INFINITY;
      const isActive = daysSinceLast <= 30;

      const stats = {
        totalOrders,
        totalSpent,
        lastOrderDate,
        isActive
      };
      this.customerStatsCache.set(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error(`Erreur stats client ${userId}:`, error);
      // Safe fallback
      const fallbackStats = {
        totalOrders: 0,
        totalSpent: 0,
        lastOrderDate: undefined,
        isActive: false
      };
      this.customerStatsCache.set(cacheKey, fallbackStats);
      return fallbackStats;
    } finally {
      this.customerStatsPromiseCache.delete(cacheKey);
    }
    })();

    this.customerStatsPromiseCache.set(cacheKey, computePromise);
    return computePromise;
  }

  // List customers with optional filters and cursor-based pagination
  async listCustomers(filters?: CustomerFilters, pageSize: number = 20, cursor?: DocumentSnapshot): Promise<ListCustomersResult> {
    try {
      // Server-side phone search (search across DB)
      if (this.isPhoneLike(filters?.searchTerm || '')) {
        const normalized = this.toE164Prefix(filters!.searchTerm!.trim());
        if (normalized) {
          // Exact equality when a full E.164 is provided
          if (normalized.isExact) {
            const exactConstraints: QueryConstraint[] = [
              where('phone', '==', normalized.prefix),
              ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
              limit(pageSize),
            ];
            const qExact = query(collection(db, this.collectionName), ...exactConstraints);
            const snapExact = await getDocs(qExact);
            const baseCustomersExact = snapExact.docs.map((d) => this.convertFirestoreToCustomer(d));
            const customersExact = await this.attachStatsToCustomers(
              baseCustomersExact,
              filters?.dateRange
            );
            return { customers: customersExact, nextCursor: undefined };
          }
          // Prefix search on phone field
          const phoneOrderConstraints: QueryConstraint[] = [
            ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
            orderBy('phone'),
            startAt(normalized.prefix),
            endAt(`${normalized.prefix}\uf8ff`),
            limit(pageSize),
          ];
          if (cursor) {
            phoneOrderConstraints.push(startAfter(cursor));
          }
          const qPhone = query(collection(db, this.collectionName), ...phoneOrderConstraints);
          const snapPhone = await getDocs(qPhone);
          const baseCustomersPhone = snapPhone.docs.map((d) => this.convertFirestoreToCustomer(d));
          const customersPhone = await this.attachStatsToCustomers(
            baseCustomersPhone,
            filters?.dateRange
          );
          const nextCursorPhone = snapPhone.docs.length > 0 ? snapPhone.docs[snapPhone.docs.length - 1] : undefined;
          return { customers: customersPhone, nextCursor: nextCursorPhone };
        }
      }

      // Server-side email search (prefix range on email)
      if (this.isEmailLike(filters?.searchTerm || '')) {
        const email = (filters!.searchTerm || '').trim();
        const emailConstraints: QueryConstraint[] = [
          ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
          orderBy('email'),
          startAt(email),
          endAt(`${email}\uf8ff`),
          limit(pageSize),
        ];
        if (cursor) {
          emailConstraints.push(startAfter(cursor));
        }
        const qEmail = query(collection(db, this.collectionName), ...emailConstraints);
        const snapEmail = await getDocs(qEmail);
        const baseCustomersEmail = snapEmail.docs.map((d) => this.convertFirestoreToCustomer(d));
        const customersEmail = await this.attachStatsToCustomers(
          baseCustomersEmail,
          filters?.dateRange
        );
        const nextCursorEmail = snapEmail.docs.length > 0 ? snapEmail.docs[snapEmail.docs.length - 1] : undefined;
        return { customers: customersEmail, nextCursor: nextCursorEmail };
      }

      // Server-side name search (prefix on firstName)
      if (filters?.searchTerm && !this.isPhoneLike(filters.searchTerm) && !this.isEmailLike(filters.searchTerm)) {
        const name = (filters!.searchTerm || '').trim();
        const nameConstraints: QueryConstraint[] = [
          ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
          orderBy('firstName'),
          startAt(name),
          endAt(`${name}\uf8ff`),
          limit(pageSize),
        ];
        if (cursor) {
          nameConstraints.push(startAfter(cursor));
        }
        const qName = query(collection(db, this.collectionName), ...nameConstraints);
        const snapName = await getDocs(qName);
        const baseCustomersName = snapName.docs.map((d) => this.convertFirestoreToCustomer(d));
        const customersName = await this.attachStatsToCustomers(
          baseCustomersName,
          filters?.dateRange
        );
        const nextCursorName = snapName.docs.length > 0 ? snapName.docs[snapName.docs.length - 1] : undefined;
        return { customers: customersName, nextCursor: nextCursorName };
      }

      const constraints: QueryConstraint[] = [];

      // Date range on user.createdAt if provided
      if (filters?.dateRange?.start && filters?.dateRange?.end) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }

      if (filters?.bannedOnly) {
        constraints.push(where('isBanned', '==', true));
      }
      // Default ordering by createdAt desc (if field exists)
      constraints.push(orderBy('createdAt', 'desc'));

      constraints.push(limit(pageSize));

      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      const q = query(collection(db, this.collectionName), ...constraints);
      const snap = await getDocs(q);

      let baseCustomers = snap.docs.map((d) => this.convertFirestoreToCustomer(d));
      const customers = await this.attachStatsToCustomers(
        baseCustomers,
        filters?.dateRange
      );

      // Client-side filters (hasEmail/hasPhone, isActive, min/max orders)
      let filtered = customers;

      if (filters?.hasEmail !== undefined) {
        filtered = filtered.filter((c) => (filters.hasEmail ? (c.email && c.email.trim()) : !(c.email && c.email.trim())));
      }


      if (filters?.hasPhone !== undefined) {
        filtered = filtered.filter((c) => (filters.hasPhone ? (c.phone && c.phone.trim()) : !(c.phone && c.phone.trim())));
      }

      if (filters?.isActive !== undefined) {
        filtered = filtered.filter((c) => (c.stats?.isActive ?? false) === filters.isActive);
      }

      if (filters?.minOrders !== undefined) {
        filtered = filtered.filter((c) => (c.stats?.totalOrders ?? 0) >= filters.minOrders!);
      }
      if (filters?.maxOrders !== undefined) {
        filtered = filtered.filter((c) => (c.stats?.totalOrders ?? 0) <= filters.maxOrders!);
      }

      const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : undefined;

      return { customers: filtered, nextCursor };
    } catch (error) {
      console.error('Erreur listCustomers:', error);
      throw new Error('Impossible de récupérer la liste des clients');
    }
  }

  // Aggregation count of customers (optionally reuse the same constraints for date range or phone search)
  async countCustomers(filters?: CustomerFilters): Promise<CountCustomersResult> {
    try {
      // Server-side phone search count
      if (this.isPhoneLike(filters?.searchTerm || '')) {
        const normalized = this.toE164Prefix((filters!.searchTerm || '').trim());
        if (normalized) {
          if (normalized.isExact) {
            const exactConstraints: QueryConstraint[] = [
              where('phone', '==', normalized.prefix),
              ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
            ];
            const qExact = query(collection(db, this.collectionName), ...exactConstraints);
            const agg = await getCountFromServer(qExact);
            return { count: Number(agg.data().count || 0) };
          }
          const phoneConstraints: QueryConstraint[] = [
            ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
            orderBy('phone'),
            startAt(normalized.prefix),
            endAt(`${normalized.prefix}\uf8ff`)
          ];
          const qPhone = query(collection(db, this.collectionName), ...phoneConstraints);
          const agg = await getCountFromServer(qPhone);
          return { count: Number(agg.data().count || 0) };
        }
      }

      // Server-side email search count
      if (this.isEmailLike(filters?.searchTerm || '')) {
        const email = (filters!.searchTerm || '').trim();
        const emailConstraints: QueryConstraint[] = [
          ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
          orderBy('email'),
          startAt(email),
          endAt(`${email}\uf8ff`)
        ];
        const qEmail = query(collection(db, this.collectionName), ...emailConstraints);
        const agg = await getCountFromServer(qEmail);
        return { count: Number(agg.data().count || 0) };
      }

      // Server-side name search count (firstName prefix)
      if (filters?.searchTerm && !this.isPhoneLike(filters.searchTerm) && !this.isEmailLike(filters.searchTerm)) {
        const name = (filters!.searchTerm || '').trim();
        const nameConstraints: QueryConstraint[] = [
          ...(filters?.bannedOnly ? [where('isBanned', '==', true)] : []),
          orderBy('firstName'),
          startAt(name),
          endAt(`${name}\uf8ff`)
        ];
        const qName = query(collection(db, this.collectionName), ...nameConstraints);
        const agg = await getCountFromServer(qName);
        return { count: Number(agg.data().count || 0) };
      }

      // Default count (optionally with date range and bannedOnly)
      const constraints: QueryConstraint[] = [];
      if (filters?.dateRange?.start && filters?.dateRange?.end) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
          where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
        );
      }
      if (filters?.bannedOnly) {
        constraints.push(where('isBanned', '==', true));
      }
      const q = query(collection(db, this.collectionName), ...constraints);
      const agg = await getCountFromServer(q);
      return { count: Number(agg.data().count || 0) };
    } catch (e) {
      console.error('Erreur countCustomers:', e);
      return { count: 0 };
    }
  }

  async updateCustomer(id: string, data: Partial<Omit<Customer, 'id' | 'stats'>>): Promise<void> {
    try {
      await updateDoc(doc(db, this.collectionName, id), {
        ...('firstName' in data ? { firstName: data.firstName } : {}),
        ...('lastName' in data ? { lastName: data.lastName } : {}),
        ...('phone' in data ? { phone: data.phone } : {}),
        ...('email' in data ? { email: data.email } : {}),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error(`Erreur updateCustomer(${id}):`, error);
      throw new Error('Impossible de mettre à jour le client');
    }
  }

  async updateAddresses(id: string, addresses: Address[]): Promise<void> {
    try {
      await updateDoc(doc(db, this.collectionName, id), {
        addresses: addresses.map(a => ({
          id: a.id,
          city: a.city,
          region: a.region ?? null,
          postalCode: a.postalCode ?? null,
          street: a.street,
          details: a.details ?? null,
          latitude: a.latitude,
          longitude: a.longitude,
          isDefault: !!a.isDefault
        })),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error(`Erreur updateAddresses(${id}):`, error);
      throw new Error('Impossible de mettre à jour les adresses');
    }
  }

  // Récupérer un client par ID (inclut email, adresses, isBanned)
  async getCustomerById(id: string): Promise<Customer | null> {
    try {
      const snap = await getDoc(doc(db, this.collectionName, id));
      if (!snap.exists()) return null;
      const data = snap.data() || {};
      const rawAddresses = Array.isArray(data.addresses) ? data.addresses : [];
      const addresses: Address[] | undefined = rawAddresses.length > 0
        ? rawAddresses.map((a: any) => ({
            id: String(a?.id ?? ''),
            city: String(a?.city ?? ''),
            region: a?.region ? String(a.region) : undefined,
            postalCode: a?.postalCode ? String(a.postalCode) : undefined,
            street: String(a?.street ?? ''),
            details: a?.details ? String(a.details) : undefined,
            latitude: typeof a?.latitude === 'number' ? a.latitude : Number(a?.latitude ?? 0),
            longitude: typeof a?.longitude === 'number' ? a.longitude : Number(a?.longitude ?? 0),
            isDefault: Boolean(a?.isDefault === true),
          }))
        : undefined;

      return {
        id: snap.id,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phone: data.phone || '',
        email: data.email || '',
        isBanned: data.isBanned === true,
        addresses,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt instanceof Date ? data.createdAt : undefined),
      };
    } catch (error) {
      console.error(`Erreur getCustomerById(${id}):`, error);
      return null;
    }
  }

  async getCustomersByIds(userIds: string[]): Promise<Customer[]> {
    try {
      const uniqueIds = Array.from(new Set(userIds.filter((id) => id && id.trim().length > 0)));
      if (uniqueIds.length === 0) {
        return [];
      }

      const customers: Customer[] = [];
      const chunkSize = 30;

      for (let index = 0; index < uniqueIds.length; index += chunkSize) {
        const chunk = uniqueIds.slice(index, index + chunkSize);
        const q = query(collection(db, this.collectionName), where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        customers.push(...snap.docs.map((docSnap) => this.convertFirestoreToCustomer(docSnap)));
      }

      return customers;
    } catch (error) {
      console.error('Erreur getCustomersByIds:', error);
      throw new Error('Impossible de récupérer les clients par identifiants');
    }
  }

  async deleteCustomers(userIds: string[], options?: { anonymizeOrders?: boolean }): Promise<{ success: boolean; deletedCount: number }> {
    try {
      const callable = httpsCallable(functions, 'deleteCustomerAuthAndData');
      const res = await callable({ userIds, anonymizeOrders: options?.anonymizeOrders ?? false });
      const data = res.data as any;
      return {
        success: !!data?.success,
        deletedCount: Number(data?.deletedCount || 0)
      };
    } catch (error: any) {
      console.error('Erreur deleteCustomers:', error?.message || error);
      throw new Error(error?.message || 'Suppression clients échouée');
    }
  }
}

const customerService = new CustomerService();
export default customerService;
